import { AuthCard } from "@/components/auth/AuthCard";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth/client";
import { getClientConfig } from "@/lib/client-config";
import { isValidEmailAddress } from "@/lib/validation/email";
import { Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = React.useState(false);
  const isEmailValid = isValidEmailAddress(email);
  const loginPath = `/auth/login${location.search}`;
  const redirectAfterSignup = React.useMemo(() => {
    const value = new URLSearchParams(location.search).get("redirect");
    return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
  }, [location.search]);

  React.useEffect(() => {
    let cancelled = false;
    void getClientConfig().then((config) => {
      if (!cancelled) setGoogleOAuthEnabled(config.auth.google);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const fallbackName = email.trim().split("@")[0] ?? "ShipPeek User";
      const result = await authClient.signUp.email({
        name: name.trim() || fallbackName,
        email: email.trim(),
        password,
      });

      if (result?.error) {
        setError(result.error.message ?? "Unable to create account.");
        return;
      }

      setMessage("Account created. Check your email to verify your address, then sign in.");
      navigate(loginPath, {
        replace: true,
        state: { message: "Account created. Verify your email before signing in." },
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="Create your account"
      description="Get started with ShipPeek TMS."
      footer={
        <p>
          Already have an account?{" "}
          <Link className="text-primary font-medium hover:underline" to={loginPath}>
            Sign in
          </Link>
        </p>
      }
    >
      {googleOAuthEnabled ? (
        <div className="mb-4 space-y-4">
          <GoogleSignInButton
            callbackURL={redirectAfterSignup}
            label="Sign up with Google"
            requestSignUp
            onError={setError}
          />
          <div className="relative text-center text-xs text-muted-foreground">
            <span className="relative z-10 bg-card px-2">or create account with email</span>
            <span className="absolute left-0 top-1/2 h-px w-full bg-border" />
          </div>
        </div>
      ) : null}

      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="name"
              placeholder="John Doe"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
              required
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="At least 8 characters"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="pl-9 pr-9"
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="pl-9"
            />
          </div>
        </div>

        {message ? (
          <p role="alert" aria-live="polite" className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            {message}
          </p>
        ) : null}
        {error ? (
          <p role="alert" aria-live="polite" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <Button type="submit" className="w-full" disabled={loading || !isEmailValid || password.length < 8}>
          {loading ? <Spinner size="sm" className="text-primary-foreground" /> : null}
          <span translate="no">{loading ? "Creating account..." : "Create account"}</span>
        </Button>
      </form>
    </AuthCard>
  );
}
